import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("polling"); // polling | paid | failed | expired
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts >= 10) { setState("failed"); return; }
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          await refresh();
          toast.success(data.type === "wallet_topup" ? "Wallet topped up successfully!" : "Payment complete — you're now active!");
          return;
        }
        if (data.status === "expired") { setState("expired"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        toast.error(apiErr(e));
        setState("failed");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div data-testid="payment-success-page" className="max-w-md w-full p-8 rounded-2xl border border-border/70 bg-card/60 text-center fade-up">
        {state === "polling" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-accent animate-spin mb-4" />
            <h2 className="font-display font-bold text-2xl mb-2">Verifying payment</h2>
            <p className="text-sm text-muted-foreground">Please wait, we're confirming with Stripe...</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-primary mb-4" />
            <h2 className="font-display font-bold text-2xl mb-2">Welcome to asiyanna!</h2>
            <p className="text-sm text-muted-foreground mb-6">Your account is now active. Sponsors have been credited.</p>
            <Button data-testid="go-to-dashboard-btn" onClick={() => nav("/dashboard")} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              Go to Dashboard
            </Button>
          </>
        )}
        {(state === "failed" || state === "expired") && (
          <>
            <XCircle className="w-14 h-14 mx-auto text-destructive mb-4" />
            <h2 className="font-display font-bold text-2xl mb-2">Payment {state}</h2>
            <p className="text-sm text-muted-foreground mb-6">Something went wrong. Try again from your dashboard.</p>
            <Button onClick={() => nav("/dashboard")} className="rounded-full">Back to Dashboard</Button>
          </>
        )}
      </div>
    </div>
  );
}
